import nanoid from 'nanoid';
import { format } from 'date-fns';

import { ClaimType, DamageDescription, MissingItemDescription, ClaimContext } from './types';
import { ConsumerActionStatus } from '../../../api/types';
import { ClaimContextForAdditionalInfoStep } from './common/AdditionalInfoStep';

export const currencies = ['EUR', 'CHF', 'CZK', 'DKK', 'GBP', 'HUF', 'NOK', 'PLN', 'RON', 'SEK'];

const countryCurrencies: Record<string, string | undefined> = {
  li: 'CHF',
  cz: 'CZK',
  dk: 'DKK',
  gb: 'GBP',
  hu: 'HUF',
  no: 'NOK',
  pl: 'PLN',
  ro: 'RON',
  se: 'SEK',
};

export const languageLabels: Record<string, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  nl: 'Dutch',
  it: 'Italian',
  pl: 'Polish',
  pt: 'Portuguese',
};

export function createEmptyInfoItem(): MissingItemDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
  };
}

export function createEmptyDamageDescription(): DamageDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
    damageDescription: '',
  };
}

export function formatDate(date: Date | string): string {
  return format(date, 'dd.MM.yyyy');
}

export function getCurrencyOfCountry(countryCode?: string | null): string {
  return countryCurrencies[countryCode || ''] || 'EUR';
}

export function isWarehouseDeliveryDateRequired(claim: ClaimContext) {
  const { useWarehouseDelivery, outcome } = getDeadline(claim) || {};

  if (!useWarehouseDelivery) {
    return false;
  }

  return outcome === 'early' || outcome === 'past';
}

export function isNonManual(claim: Pick<ClaimContext, 'shipmentId'>) {
  return !!claim.shipmentId;
}

function getDeadline(claim: ClaimContext) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].deadline : null;
}

export function getClaimableWarning(claim: Pick<ClaimContext, 'submissionInfo' | 'type'>) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].warning : null;
}

export function isClaimWithMissingTrackingData(
  claim: Pick<ClaimContext, 'submissionInfo' | 'type' | 'shipmentId' | 'entryPoint'>
) {
  return getClaimableWarning(claim) === 'no_required_tracking_data';
}

export function isManualDraft(claim: Pick<ClaimContext, 'shipmentId' | 'draftId'>) {
  return claim.draftId && !claim.shipmentId;
}

export function isClaimOutsideOfDeadline(claim: ClaimContext) {
  const { outcome } = getDeadline(claim) || {};
  // it's better to not compare against "within" because for manual flow we don't have deadlines
  return outcome === 'early' || outcome === 'past';
}

export function isClaimWithMailboxDeliveryWarning(claim: ClaimContext) {
  return getClaimableWarning(claim) === 'mailbox_delivery_no_liability';
}

export function isShipmentDelivered(claim: ClaimContext) {
  return claim.isDelivered;
}

export function isInvestigationClaim(claim: ClaimContext) {
  return claim.type === 'investigation';
}

export function isPartialDamageClaim(claim: ClaimContext) {
  return claim.type === 'partial_damage';
}

export function isWholeDamageClaim(claim: ClaimContext) {
  return claim.type === 'whole_damage';
}

export function isMissingItemClaim(claim: ClaimContext) {
  return claim.type === 'missing_item';
}

export function isCashOnDeliveryClaim(claim: ClaimContext) {
  return claim.type === 'cash_on_delivery';
}

export function isClaimsCenter(claim: Pick<ClaimContext, 'entryPoint'>) {
  return claim.entryPoint === 'claims-center';
}

export function isManualClaim(claim: Pick<ClaimContext, 'entryPoint' | 'shipmentId'>) {
  return isClaimsCenter(claim) && !isNonManual(claim);
}

export function isClaimsCenterAndNonManual(claim: ClaimContext) {
  return isClaimsCenter(claim) && isNonManual(claim);
}

export function isAdditionalInfoRequired(context: ClaimContext) {
  const { isAdditionalInfoRequired } = computeAdditionalInfoRequirements(context);
  return isAdditionalInfoRequired;
}

export function isInvoiceRequired(context: Pick<ClaimContext, 'type'>) {
  return context.type !== ClaimType.CASH_ON_DELIVERY;
}

export function computeAdditionalInfoRequirements(context: ClaimContextForAdditionalInfoStep) {
  const {
    isCarrierCaseIdAndCarrierDamageReportRequired: isCarrierCaseIdAndCarrierDamageReportRequiredForCarrier = false,
    isDisplayCustomerPhoneNumberInputEnabled: isDisplayCustomerPhoneNumberInputEnabledForCarrier = false,
    isWeightRequired: isWeightRequiredForCarrier = false,
    isDisplayCarrierCaseIdInputEnabled: isDisplayCarrierCaseIdInputEnabledForCarrier = false,
  } = context.carrier || {};

  const isMissingItemClaim = !!context.type && context.type === ClaimType.MISSING_ITEM;
  const isDamageInfoRequired =
    !!context.type && [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE].includes(context.type);
  const isDamageOrMissingItemClaim = isDamageInfoRequired || isMissingItemClaim;
  const isCashOnDeliveryClaim = context.type === ClaimType.CASH_ON_DELIVERY;

  const isCarrierCaseIdAndCarrierDamageReportRequired =
    isCarrierCaseIdAndCarrierDamageReportRequiredForCarrier && isDamageOrMissingItemClaim;

  const isDataProcessingConsentRequired = context.claimRequirements?.dataProcessingConsent === 'required';

  const isPicturesOfMissingItemRequired =
    context.claimRequirements?.isPicturesOfMissingItemRequired && isMissingItemClaim;

  const isDamagePhotosRequired = context.claimRequirements?.isDamagePhotosRequired && isDamageInfoRequired;

  const isPictureUploadLinkStepRequired = isPictureUploadLinkStepEnabled(context);

  const isWeightRequired = isWeightRequiredForCarrier && !isCashOnDeliveryClaim;
  const isDisplayCarrierCaseIdInputEnabled = isDisplayCarrierCaseIdInputEnabledForCarrier && !isCashOnDeliveryClaim;
  const isRecipientPhoneNumberRequired = isDisplayCustomerPhoneNumberInputEnabledForCarrier && !isCashOnDeliveryClaim;
  const isCarrierSpecificDataRequired =
    isWeightRequired ||
    isRecipientPhoneNumberRequired ||
    isCarrierCaseIdAndCarrierDamageReportRequired ||
    isDataProcessingConsentRequired;

  const declarationOfRecipientRequirement =
    context.type === ClaimType.INVESTIGATION ? (context.claimRequirements?.declarationOfRecipient ?? 'none') : 'none';

  const isAdditionalInfoRequired =
    declarationOfRecipientRequirement !== 'none' ||
    isPictureUploadLinkStepRequired ||
    isCarrierSpecificDataRequired ||
    isDamageInfoRequired ||
    isCashOnDeliveryClaim ||
    isMissingItemClaim;

  return {
    isDisplayCarrierCaseIdInputEnabled,
    isWeightRequired,
    isRecipientPhoneNumberRequired,
    isPicturesOfMissingItemRequired,
    isDamagePhotosRequired,
    isCarrierCaseIdAndCarrierDamageReportRequired,
    isPictureUploadLinkStepRequired,
    isCarrierSpecificDataRequired,
    isAdditionalInfoRequired,
    isDamageInfoRequired,
    declarationOfRecipientRequirement,
    isDataProcessingConsentRequired,
  };
}

export function isPictureUploadLinkStepEnabled(
  context: Pick<ClaimContext, 'shopSettings' | 'picturesOfDamage' | 'picturesOfMissingItem' | 'type' | 'shipmentId'>
) {
  return (
    context.type &&
    [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE, ClaimType.MISSING_ITEM].includes(context.type) &&
    context.shopSettings.isPhotoCollectionAppEnabled &&
    !!context.shipmentId
  );
}

export function isDorSigningLinkStepEnabled(context: ClaimContext) {
  return (
    (context.shouldForceShowEDORStep || context.consumerActionStatus !== ConsumerActionStatus.COMPLETED) &&
    isShipmentDelivered(context) &&
    context.shopSettings.isDORSigningLinkEnabled &&
    isNonManual(context) &&
    (context.claimRequirements?.declarationOfRecipient === 'required' ||
      context.claimRequirements?.declarationOfRecipient === 'optional')
  );
}
